import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Theme {
  background: string;
  cardBackground: string;
  text: string;
  textSecondary: string;
  border: string;
  buttonBackground: string;
  buttonText: string;
  buttonBorder: string;
  activeBackground: string;
  activeText: string;
  navbarBackground: string;
  hoverBackground: string;
  inputBackground: string;
  inputBorder: string;
  accent: string;
  // Status colors
  success: string;
  successLight: string;
  successDark: string;
  warning: string;
  warningLight: string;
  warningDark: string;
  error: string;
  errorLight: string;
  errorDark: string;
  info: string;
  infoLight: string;
  infoDark: string;
  // Additional semantic colors
  blue: string;
  purple: string;
  gray: string;
  lightGray: string;
  codeBackground: string;
  tableBorder: string;
  tableHeaderBg: string;
  dangerButton: string;
  dangerButtonHover: string;
  secondaryButton: string;
  secondaryButtonHover: string;
}

const lightTheme: Theme = {
  background: '#f5f6f8',
  cardBackground: '#ffffff',
  text: '#1a202c',
  textSecondary: '#718096',
  border: '#e5e7eb',
  buttonBackground: '#111827',
  buttonText: '#ffffff',
  buttonBorder: '#111827',
  activeBackground: '#e5e7eb',
  activeText: '#111827',
  navbarBackground: '#ffffff',
  hoverBackground: '#f9fafb',
  inputBackground: '#ffffff',
  inputBorder: '#d1d5db',
  accent: '#111827',
  // Status colors
  success: '#48bb78',
  successLight: '#d1fae5',
  successDark: '#065f46',
  warning: '#ed8936',
  warningLight: '#fef3c7',
  warningDark: '#92400e',
  error: '#f56565',
  errorLight: '#fff5f5',
  errorDark: '#c53030',
  info: '#4299e1',
  infoLight: '#dbeafe',
  infoDark: '#1e40af',
  // Additional semantic colors
  blue: '#4299e1',
  purple: '#805ad5',
  gray: '#718096',
  lightGray: '#a0aec0',
  codeBackground: '#edf2f7',
  tableBorder: '#edf2f7',
  tableHeaderBg: '#f8fafc',
  dangerButton: '#ef4444',
  dangerButtonHover: '#dc2626',
  secondaryButton: '#667eea',
  secondaryButtonHover: '#5a67d8',
};

const darkTheme: Theme = {
  background: '#091921',
  cardBackground: '#0d2530',
  text: '#e5e7eb',
  textSecondary: '#9ca3af',
  border: '#1e3a47',
  buttonBackground: '#2BC48C',
  buttonText: '#091921',
  buttonBorder: '#2BC48C',
  activeBackground: '#2BC48C',
  activeText: '#091921',
  navbarBackground: '#0a1f28',
  hoverBackground: '#112d3a',
  inputBackground: '#0d2530',
  inputBorder: '#1e3a47',
  accent: '#2BC48C',
  // Status colors
  success: '#10b981',
  successLight: '#064e3b',
  successDark: '#6ee7b7',
  warning: '#f59e0b',
  warningLight: '#78350f',
  warningDark: '#fcd34d',
  error: '#ef4444',
  errorLight: '#7f1d1d',
  errorDark: '#fca5a5',
  info: '#3b82f6',
  infoLight: '#1e3a8a',
  infoDark: '#93c5fd',
  // Additional semantic colors
  blue: '#60a5fa',
  purple: '#a78bfa',
  gray: '#6b7280',
  lightGray: '#9ca3af',
  codeBackground: '#1e293b',
  tableBorder: '#1e3a47',
  tableHeaderBg: '#0a1f28',
  dangerButton: '#ef4444',
  dangerButtonHover: '#dc2626',
  secondaryButton: '#2BC48C',
  secondaryButtonHover: '#25a576',
};

interface ThemeContextType {
  theme: Theme;
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
